import { Connection, PublicKey, SystemProgram, Transaction } from '@solana/web3.js';
import { solanaUtils } from './solanaUtils';
import { getSolBalance } from './transactionUtils';

export interface WithdrawResult {
  success: boolean;
  signature?: string;
  error?: string;
  amountWithdrawn?: number;
}

/**
 * Validate if a wallet address is valid
 */
export function validateWalletAddress(address: string): { valid: boolean; error?: string } {
  try {
    new PublicKey(address);
    return { valid: true };
  } catch (error) {
    return { 
      valid: false, 
      error: 'Invalid wallet address format' 
    };
  }
}

/**
 * Calculate maximum withdrawable amount (leave some SOL for fees)
 */
export async function getMaxWithdrawableAmount(
  connection: Connection,
  walletAddress: string,
  logger?: (type: string, data: any) => void
): Promise<number> {
  try {
    const balance = await getSolBalance(connection, walletAddress, logger);
    
    // Reserve 0.001 SOL for transaction fees and account rent
    const reserveAmount = 0.001;
    const maxWithdrawable = Math.max(0, balance - reserveAmount);
    
    logger?.('MaxWithdrawableCalculated', { 
      totalBalance: balance, 
      reserveAmount, 
      maxWithdrawable 
    });
    
    return maxWithdrawable;
  } catch (error: any) {
    logger?.('MaxWithdrawableError', { error: error.message });
    return 0;
  }
}

/**
 * Create a SOL transfer transaction
 */
export async function createWithdrawTransaction(
  fromWalletAddress: string,
  toWalletAddress: string,
  amountSol: number
): Promise<Transaction> {
  const connection = solanaUtils.createConnection();
  const fromPubkey = new PublicKey(fromWalletAddress);
  const toPubkey = new PublicKey(toWalletAddress);
  
  // Create transaction
  const transaction = new Transaction();
  
  // Add transfer instruction
  const transferInstruction = SystemProgram.transfer({
    fromPubkey,
    toPubkey,
    lamports: Math.floor(amountSol * 1_000_000_000) // Convert SOL to lamports
  });
  
  transaction.add(transferInstruction);
  
  // Get recent blockhash
  const { blockhash } = await connection.getLatestBlockhash('confirmed');
  transaction.recentBlockhash = blockhash;
  transaction.feePayer = fromPubkey;
  
  return transaction;
}

/**
 * Execute withdrawal transaction
 */
export async function executeWithdraw(
  connection: Connection,
  fromWalletAddress: string,
  toWalletAddress: string,
  amountSol: number,
  sendTransaction: any,
  waitForConfirmation: (signature: string) => Promise<{ confirmed: boolean }>,
  logger?: (type: string, data: any) => void
): Promise<WithdrawResult> {
  try {
    // Validate inputs
    const toAddressValidation = validateWalletAddress(toWalletAddress);
    if (!toAddressValidation.valid) {
      return {
        success: false,
        error: toAddressValidation.error
      };
    }
    
    // Check if amount is valid
    if (amountSol <= 0) {
      return {
        success: false,
        error: 'Withdrawal amount must be greater than 0'
      };
    }
    
    // Check if user has sufficient balance and adjust for transaction fees
    const currentBalance = await getSolBalance(connection, fromWalletAddress, logger);
    
    if (amountSol > currentBalance) {
      return {
        success: false,
        error: `Insufficient balance. Available: ${currentBalance.toFixed(4)} SOL`
      };
    }
    
    // If trying to withdraw the full balance, leave a small amount for transaction fees
    let adjustedAmount = amountSol;
    const estimatedFee = 0.000005; // Typical Solana transaction fee
    
    if (Math.abs(amountSol - currentBalance) < 0.000001) {
      // User is trying to withdraw full balance, adjust for fees
      adjustedAmount = currentBalance - estimatedFee;
      logger?.('AdjustedForFees', { 
        originalAmount: amountSol, 
        adjustedAmount, 
        estimatedFee 
      });
    }
    
    logger?.('WithdrawStart', {
      from: fromWalletAddress.slice(0, 8) + '...',
      to: toWalletAddress.slice(0, 8) + '...',
      amount: amountSol,
      adjustedAmount,
      currentBalance
    });
    
    // Create withdrawal transaction with adjusted amount
    const transaction = await createWithdrawTransaction(
      fromWalletAddress,
      toWalletAddress,
      adjustedAmount
    );
    
    // Execute transaction
    const receipt = await sendTransaction({
      transaction,
      connection,
      uiOptions: { showWalletUIs: false }
    });
    
    logger?.('WithdrawSubmitted', { 
      signature: receipt.signature,
      requestedAmount: amountSol,
      actualAmount: adjustedAmount
    });
    
    // Wait for confirmation
    const confirmationResult = await waitForConfirmation(receipt.signature);
    
    if (confirmationResult.confirmed) {
      logger?.('WithdrawSuccess', { 
        signature: receipt.signature,
        amount: amountSol,
        solscanUrl: `https://solscan.io/tx/${receipt.signature}`
      });
      
      return {
        success: true,
        signature: receipt.signature,
        amountWithdrawn: adjustedAmount
      };
    } else {
      logger?.('WithdrawFailed', { 
        signature: receipt.signature,
        error: 'Transaction failed to confirm'
      });
      
      return {
        success: false,
        error: 'Transaction failed to confirm on blockchain'
      };
    }
    
  } catch (error: any) {
    logger?.('WithdrawError', { error: error.message });
    
    return {
      success: false,
      error: error.message || 'Withdrawal failed'
    };
  }
}

/**
 * Validate withdrawal amount against balance
 */
export async function validateWithdrawAmount(
  connection: Connection,
  walletAddress: string,
  amount: number,
  logger?: (type: string, data: any) => void
): Promise<{ valid: boolean; error?: string; maxAmount?: number }> {
  try {
    if (amount <= 0) {
      return {
        valid: false,
        error: 'Amount must be greater than 0'
      };
    }
    
    const maxWithdrawable = await getMaxWithdrawableAmount(connection, walletAddress, logger);
    
    if (amount > maxWithdrawable) {
      return {
        valid: false,
        error: `Amount exceeds maximum withdrawable balance`,
        maxAmount: maxWithdrawable
      };
    }
    
    return { valid: true };
    
  } catch (error: any) {
    logger?.('ValidateWithdrawAmountError', { error: error.message });
    return {
      valid: false,
      error: 'Unable to validate withdrawal amount'
    };
  }
} 